import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CssBaseline from '@mui/material/CssBaseline';
import { styled } from '@mui/system';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import React from 'react';
import { createRoot } from 'react-dom/client';
import './../utils/wdyr';

import 'regenerator-runtime/runtime';
import supportedBrowsers from '../../static/supportedBrowsers';
import App from './AppContainer';
import BrowserNotSupported from './BrowserNotSupported';
import { ColorModeContextProvider } from './ColorModeContext';
import Root from './Root';
import { getApplicationTitle } from 'utils/deploymentEnvironment';

import '@xyflow/react/dist/style.css';
import 'katex/dist/katex.min.css';
import 'react-chart-editor/lib/react-chart-editor.css';

declare const SENTRY_ENABLED: boolean;
declare const SENTRY_ENVIRONMENT: string;
declare const BUILD_GIT_SHA: string;
declare const BUILD_TIMESTAMP: string;

declare global {
  interface Window {
    __BUILD_INFO__?: {
      gitSha: string;
      buildTimestamp: string;
    };
  }
}

if (SENTRY_ENABLED) {
  Sentry.init({
    dsn: 'https://45d462dcc94644bab369d01ef984cc31@o158663.ingest.sentry.io/1417983',
    attachStacktrace: true,
    integrations: [
      new Integrations.CaptureConsole({
        levels: ['error'],
      }),
    ],
    environment: SENTRY_ENVIRONMENT,
  });
}

if (typeof window !== 'undefined') {
  window.__BUILD_INFO__ = {
    gitSha: BUILD_GIT_SHA,
    buildTimestamp: BUILD_TIMESTAMP,
  };
  // Under-the-hood debug log for quick deployment verification.
  // eslint-disable-next-line no-console
  console.debug('[build-info]', window.__BUILD_INFO__);
}

if (typeof document !== 'undefined') {
  document.title = getApplicationTitle('Nucleai');
}

const isBrowserSupported = () => supportedBrowsers.test(navigator.userAgent);

const StyledMaterialDesignContent = styled(MaterialDesignContent)(({ theme }) => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: theme.palette.grey[800],
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.palette.grey[800],
  },
  '&.notistack-MuiContent-default': {
    backgroundColor: theme.palette.grey[800],
  },
}));

const Index: React.FunctionComponent<React.PropsWithChildren<unknown>> = () => (
  <ColorModeContextProvider>
    <SnackbarProvider
      autoHideDuration={5000}
      maxSnack={4}
      iconVariant={{
        success: <CheckCircleIcon fontSize="small" sx={{ color: 'success.light', mr: 1 }} />,
      }}
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        default: StyledMaterialDesignContent,
      }}
    >
      <Root>
        {isBrowserSupported() ? (
          <>
            <CssBaseline enableColorScheme />
            <App />
          </>
        ) : (
          <BrowserNotSupported />
        )}
      </Root>
    </SnackbarProvider>
  </ColorModeContextProvider>
);

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(<Index />);
